import React, { createContext, useState, useContext, useEffect } from "react";
import { authAPI } from "../services/api";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Helper function to check if token is expired (24 hours)
const isTokenExpired = (tokenTimestamp) => {
  if (!tokenTimestamp) return true;
  const now = Date.now();
  const twentyFourHours = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
  return (now - tokenTimestamp) > twentyFourHours;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for existing session on mount and periodically
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("talsolution_token");
      const storedUser = localStorage.getItem("talsolution_user");
      const tokenTimestamp = localStorage.getItem("talsolution_token_timestamp");
      
      // Check if token is expired (24 hours)
      if (tokenTimestamp && isTokenExpired(parseInt(tokenTimestamp))) {
        // Token expired, clear storage and redirect
        localStorage.removeItem("talsolution_token");
        localStorage.removeItem("talsolution_user");
        localStorage.removeItem("talsolution_token_timestamp");
        setUser(null);
        setLoading(false);
        // Redirect will be handled by the component or interceptor
        return;
      }
      
      if (token && storedUser) {
        try {
          // Verify token is still valid with backend
          const response = await authAPI.getMe();
          if (response.data.success) {
            setUser(response.data.data.user);
          } else {
            // Token invalid, clear storage
            localStorage.removeItem("talsolution_token");
            localStorage.removeItem("talsolution_user");
            localStorage.removeItem("talsolution_token_timestamp");
            setUser(null);
          }
        } catch (error) {
          // Token expired or invalid
          localStorage.removeItem("talsolution_token");
          localStorage.removeItem("talsolution_user");
          localStorage.removeItem("talsolution_token_timestamp");
          setUser(null);
        }
      }
      setLoading(false);
    };

    checkAuth();

    // Check token expiration every 5 minutes
    const interval = setInterval(() => {
      const tokenTimestamp = localStorage.getItem("talsolution_token_timestamp");
      if (tokenTimestamp && isTokenExpired(parseInt(tokenTimestamp))) {
        // Token expired, clear storage
        localStorage.removeItem("talsolution_token");
        localStorage.removeItem("talsolution_user");
        localStorage.removeItem("talsolution_token_timestamp");
        setUser(null);
        // Redirect to login if not already there
        if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
          window.location.href = '/login';
        }
      }
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => clearInterval(interval);
  }, []);

  const login = (userData, token) => {
    setUser(userData);
    localStorage.setItem("talsolution_user", JSON.stringify(userData));
    if (token) {
      localStorage.setItem("talsolution_token", token);
      // Store token timestamp for 24-hour expiration check
      localStorage.setItem("talsolution_token_timestamp", Date.now().toString());
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem("talsolution_user");
    localStorage.removeItem("talsolution_token");
    localStorage.removeItem("talsolution_token_timestamp");
    // Redirect to login page
    window.location.href = '/login';
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem("talsolution_user", JSON.stringify(userData));
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
